import React, { useEffect, useState, useContext, useMemo } from "react";
import QRCode from "qrcode.react";
import toastError from "../../errors/toastError";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  DialogActions,
  DialogTitle,
  IconButton,
  Box,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PhoneAndroidIcon from "@material-ui/icons/PhoneAndroid";
import SettingsIcon from "@material-ui/icons/Settings";
import PhonelinkIcon from "@material-ui/icons/Phonelink";
import CropFreeIcon from "@material-ui/icons/CropFree";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const QR_EXPIRE_SECONDS = 40;

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: 720,
    width: "100%",
    margin: theme.spacing(2),
    borderRadius: 12,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      maxWidth: "100%",
    },
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2, 2, 1),
    "& h2": {
      fontSize: "1.125rem",
      fontWeight: 600,
      color: theme.palette.grey[800],
    },
  },
  body: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    padding: theme.spacing(1, 2, 2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  stepsColumn: {
    flex: "1 1 280px",
    maxWidth: "100%",
    paddingRight: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignSelf: "stretch",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
      alignItems: "center",
    },
  },
  stepsInner: {
    width: "100%",
    maxWidth: 400,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
  },
  qrColumn: {
    flex: "1 1 260px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    alignSelf: "stretch",
  },
  stepRow: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2.25),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  stepBadge: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#1a237e",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: 600,
    flexShrink: 0,
  },
  stepIconWrap: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1.25),
    display: "flex",
    alignItems: "center",
    paddingTop: 2,
    color: "#ef5350",
  },
  stepText: {
    fontSize: "0.875rem",
    lineHeight: 1.45,
    color: theme.palette.grey[800],
    paddingTop: 2,
  },
  qrBox: {
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: 12,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "1px solid rgba(0,0,0,0.06)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  hintBelowQr: {
    marginTop: theme.spacing(1.5),
    fontSize: "0.8125rem",
    color: theme.palette.grey[600],
    textAlign: "center",
    maxWidth: 280,
    lineHeight: 1.4,
  },
  timerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    gap: theme.spacing(1.5),
  },
  timerLabel: {
    fontSize: "0.875rem",
    color: theme.palette.grey[600],
  },
  timerWarning: {
    marginTop: theme.spacing(1),
    fontSize: "0.8125rem",
    textAlign: "center",
    maxWidth: 320,
    lineHeight: 1.4,
  },
  warningUrgent: {
    color: "#e65100",
  },
  warningExpired: {
    color: "#c62828",
  },
  loadingText: {
    color: theme.palette.text.secondary,
    textAlign: "center",
    padding: theme.spacing(3),
  },
}));

const stepIcons = [PhoneAndroidIcon, SettingsIcon, PhonelinkIcon, CropFreeIcon];

function QrCountdownRing({ secondsLeft, total }) {
  const radius = 22;
  const circumference = useMemo(() => 2 * Math.PI * radius, []);
  const progress = total > 0 ? Math.max(0, Math.min(1, secondsLeft / total)) : 0;
  const strokeDashoffset = circumference * (1 - progress);
  const strokeColor =
    secondsLeft === 0 ? "#bdbdbd" : secondsLeft <= 10 ? "#ff9800" : "#4caf50";

  return (
    <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden>
      <circle
        cx="28"
        cy="28"
        r={radius}
        fill="none"
        stroke="#e0e0e0"
        strokeWidth="4"
      />
      <circle
        cx="28"
        cy="28"
        r={radius}
        fill="none"
        stroke={strokeColor}
        strokeWidth="4"
        strokeLinecap="round"
        transform="rotate(-90 28 28)"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        style={{ transition: "stroke-dashoffset 1s linear, stroke 0.3s" }}
      />
      <text
        x="28"
        y="32"
        textAnchor="middle"
        fontSize="13"
        fontWeight={500}
        fill="#757575"
        fontFamily="Roboto, Helvetica, Arial, sans-serif"
      >
        {secondsLeft}s
      </text>
    </svg>
  );
}

function renderQrContent(qrCode, size = 240) {
  if (!qrCode) return null;
  if (qrCode.startsWith("2@")) {
    const qrWithoutPrefix = qrCode.substring(2);
    if (
      qrWithoutPrefix.match(/^[A-Za-z0-9+/=]+$/) &&
      (qrWithoutPrefix.startsWith("iVBORw0KGgo") ||
        qrWithoutPrefix.startsWith("/9j/"))
    ) {
      return (
        <img
          src={`data:image/png;base64,${qrWithoutPrefix}`}
          alt="QR Code"
          style={{
            maxWidth: size,
            maxHeight: size,
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      );
    }
    return (
      <QRCode
        value={qrCode}
        size={size}
        style={{ maxWidth: "100%", height: "auto" }}
      />
    );
  }
  if (qrCode.startsWith("data:")) {
    return (
      <img
        src={qrCode}
        alt="QR Code"
        style={{
          maxWidth: size,
          maxHeight: size,
          width: "100%",
          height: "auto",
          display: "block",
        }}
      />
    );
  }
  if (
    qrCode.match(/^[A-Za-z0-9+/=]+$/) &&
    (qrCode.startsWith("iVBORw0KGgo") || qrCode.startsWith("/9j/"))
  ) {
    return (
      <img
        src={`data:image/png;base64,${qrCode}`}
        alt="QR Code"
        style={{
          maxWidth: size,
          maxHeight: size,
          width: "100%",
          height: "auto",
          display: "block",
        }}
      />
    );
  }
  return (
    <QRCode
      value={qrCode}
      size={size}
      style={{ maxWidth: "100%", height: "auto" }}
    />
  );
}

const QrcodeModal = ({ open, onClose, whatsAppId, onOpenPairPhone, channel }) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState("");
  const [whatsappData, setWhatsappData] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(QR_EXPIRE_SECONDS);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`/whatsapp-admin/${whatsAppId}?session=0`);
        setQrCode(data.qrcode);
        setWhatsappData(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    if (!whatsAppId) return;
    const companyId = user.companyId;

    const onWhatsappData = (data) => {
      if (data.action !== "update" || !data.session) return;

      const sid = data.session.id;
      if (String(sid) !== String(whatsAppId)) return;

      const nextQr = data.session.qrcode;
      setQrCode(nextQr != null ? String(nextQr) : "");
      setWhatsappData((prev) =>
        prev ? { ...prev, ...data.session } : data.session
      );

      const st = String(data.session.status || "").toUpperCase();
      // Fechar só quando a sessão estiver conectada (evita fechar com null/"" enquanto aguarda QR)
      if (st === "CONNECTED") {
        onClose();
      }
    };
    socket.on(`company-${companyId}-whatsappSession`, onWhatsappData);

    return () => {
      socket.off(`company-${companyId}-whatsappSession`, onWhatsappData);
    };
  }, [whatsAppId, onClose, user.companyId, socket]);

  // Avisa backend que esta modal está aberta — habilita cron QR refresh
  // só pra esta conn enquanto o usuário está olhando. Sem isso, cron
  // refrescaria QR de TODAS conns wuzapi em qrcode, gerando lock contention
  // massivo no Postgres (incidente 31/05: 18 conns simultâneas → load 50+).
  useEffect(() => {
    if (!open || !whatsAppId || !socket) return;
    socket.emit("joinQrRoom", whatsAppId);
    return () => {
      socket.emit("leaveQrRoom", whatsAppId);
    };
  }, [open, whatsAppId, socket]);

  useEffect(() => {
    if (!open) {
      setSecondsLeft(QR_EXPIRE_SECONDS);
      return;
    }
    if (qrCode) {
      setSecondsLeft(QR_EXPIRE_SECONDS);
    }
  }, [open, qrCode]);

  useEffect(() => {
    if (!open || !qrCode) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => (s <= 0 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [open, qrCode]);

  const steps = [1, 2, 3, 4].map((n) => ({
    n,
    text: i18n.t(`qrCode.step${n}`),
    Icon: stepIcons[n - 1],
  }));

  /** WhatsApp (Baileys/Hineken) e WuzAPI/whatsmeow: API de pareamento por telefone */
  const showPairPhoneFromQr =
    (channel &&
      ["wuzapi", "whatsmeow", "whatsapp"].includes(channel.toLowerCase())) ||
    (whatsappData?.channel &&
      ["wuzapi", "whatsmeow", "whatsapp"].includes(
        whatsappData.channel.toLowerCase()
      ));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="qr-code-dialog-title"
      maxWidth={false}
    >
      <DialogTitle id="qr-code-dialog-title" disableTypography className={classes.titleRow}>
        <Typography variant="h6" component="h2">
          {i18n.t("qrCode.modalTitle")}
        </Typography>
        <IconButton aria-label="close" size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 0 }}>
        <Box className={classes.body}>
          <Box className={classes.stepsColumn}>
            <Box className={classes.stepsInner}>
              {steps.map(({ n, text, Icon }) => (
                <Box key={n} className={classes.stepRow}>
                  <Box className={classes.stepBadge}>{n}</Box>
                  <Box className={classes.stepIconWrap}>
                    <Icon style={{ fontSize: 22 }} />
                  </Box>
                  <Typography className={classes.stepText} component="div">
                    {text}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box className={classes.qrColumn}>
            {qrCode ? (
              <>
                <Box className={classes.qrBox}>{renderQrContent(qrCode, 240)}</Box>
                <Typography className={classes.hintBelowQr} component="p">
                  {i18n.t("qrCode.hintBelowQr")}
                </Typography>

                <Box className={classes.timerRow}>
                  <QrCountdownRing
                    secondsLeft={secondsLeft}
                    total={QR_EXPIRE_SECONDS}
                  />
                  <Typography className={classes.timerLabel} component="span">
                    {i18n.t("qrCode.remainingTime")}
                  </Typography>
                </Box>

                {secondsLeft > 0 && secondsLeft <= 10 && (
                  <Typography
                    className={`${classes.timerWarning} ${classes.warningUrgent}`}
                    component="p"
                  >
                    {i18n.t("qrCode.expireSoon")}
                  </Typography>
                )}
                {secondsLeft === 0 && (
                  <Typography
                    className={`${classes.timerWarning} ${classes.warningExpired}`}
                    component="p"
                  >
                    {i18n.t("qrCode.expiredHint")}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body1" className={classes.loadingText}>
                {i18n.t("qrCode.waiting")}
              </Typography>
            )}
          </Box>
        </Box>

        {showPairPhoneFromQr && onOpenPairPhone && (
          <Box style={{ textAlign: "center", padding: "0 16px 8px" }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                onClose();
                onOpenPairPhone();
              }}
              fullWidth
            >
              Conectar via Telefone
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          {i18n.t("qrCode.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(QrcodeModal);
